// ========================================
// 📱 HOOK: useMediaQuery
// Detecta media queries en React de forma segura
// ========================================

import { useState, useEffect } from 'react';

/**
 * Hook personalizado para evaluar media queries
 * @param {string} query - Media query a evaluar (ej: '(max-width: 768px)')
 * @returns {boolean} - True si la media query coincide
 * 
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
 */
export function useMediaQuery(query) {
  // Estado inicial seguro (para SSR)
  const [matches, setMatches] = useState(() => {
    // Verificar si window existe (para SSR/SSG)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false; // Valor por defecto seguro para SSR
  });

  useEffect(() => {
    // Verificar que estamos en cliente
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(query);
    
    // Actualizar estado si el valor inicial es incorrecto
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    // Listener para cambios
    const listener = (event) => {
      setMatches(event.matches);
    };

    // Agregar listener (compatible con todos los navegadores)
    if (media.addEventListener) {
      media.addEventListener('change', listener);
    } else {
      // Fallback para navegadores antiguos
      media.addListener(listener);
    }

    // Cleanup
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, [query, matches]);

  return matches;
}

/**
 * Hook de atajo para detectar móvil
 * @returns {boolean} - True si es móvil (<= 768px)
 */
export function useIsMobile() {
  return useMediaQuery('(max-width: 768px)');
}

/**
 * Hook de atajo para detectar tablet
 * @returns {boolean} - True si es tablet (769px - 1024px)
 */
export function useIsTablet() {
  return useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
}

/**
 * Hook de atajo para detectar escritorio
 * @returns {boolean} - True si es escritorio (> 1024px)
 */
export function useIsDesktop() {
  return useMediaQuery('(min-width: 1025px)');
}

/**
 * Hook para detectar orientación
 * @returns {string} - 'portrait' o 'landscape'
 */
export function useOrientation() {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  return isPortrait ? 'portrait' : 'landscape';
}

/**
 * Hook para detectar si es dispositivo táctil
 * @returns {boolean} - True si el dispositivo soporta touch
 */
export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore
      navigator.msMaxTouchPoints > 0
    );
  }, []);

  return isTouch;
}

// Exportar todo por defecto también
export default useMediaQuery;